<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import BusinessForm from '@/components/funnelForms/BusinessForm.vue'
import ForMeForm from '@/components/funnelForms/ForMeForm.vue'
import GiftFormCZ from '@/components/funnelForms/GiftFormCZ.vue'
import GiftFormSK from '@/components/funnelForms/GiftFormSK.vue'
import StudentForm from '@/components/funnelForms/StudentForm.vue'
import { useFormDataStore } from '@/stores/FormDataStore'
import { useSettingsStore } from '@/stores/SettingsStore'
import { useSubscriptionsStore } from '@/stores/SubscriptionsStore'
import { useVoucherStore } from '@/stores/VoucherStore'
import { useHelpers } from '@/useHelpers'

const dataStore = useFormDataStore()
const subscriptionStore = useSubscriptionsStore()
const voucherStore = useVoucherStore()

const settingsStore = useSettingsStore()
const subscription = computed(() => dataStore.selectedSubscriptionWithVariant)

const { t } = useI18n()
const { formatMoney, ucFirst } = useHelpers()

const interval = computed(() => {
  let interval = {}
  switch (subscription.value.length) {
    case 730:
      interval.short = t('two years')
      interval.long = ucFirst(t('two yearly payment'))
      break
    case 365:
      interval.short = t('year')
      interval.long = t('yearly payment')
      break
    case 7:
      interval.short = t('week')
      interval.long = t('weekly payment')
      break
    default:
      interval.short = t('month')
      interval.long = t('monthly payment')
      break
  }
  return interval
})

const selectedForm = computed(() => {
  switch (window.init.funnelUrlKey) {
    case 'gift':
      return settingsStore.getVersion === 'CZ' ? GiftFormCZ : GiftFormSK
    case 'student':
    case 'prolongacia-predplatneho-student':
      return StudentForm
    case 'business':
      return BusinessForm
    default:
      return ForMeForm
  }
})
</script>

<template>
  <form id="mainForm"></form>

  <main class="page-sales-funnel">
    <div class="page-sales-funnel__header">
      <div class="container">
        <div class="page-sales-funnel__header-wrapper">
          <div class="page-sales-funnel__subscription-name">
            {{ subscription.user_label }}
          </div>

          <div class="page-sales-funnel__header-details">
            <span v-if="subscriptionStore.amount" class="page-sales-funnel__price">
              {{ subscriptionStore.amount }} x
              {{
                formatMoney(
                  subscriptionStore.subscriptionPrice(subscription.code, voucherStore.voucher)
                )
              }}
              = {{ formatMoney(subscriptionStore.finalPrice) }} / {{ interval.short }}
            </span>

            <span v-else class="page-sales-funnel__price">
              {{ formatMoney(subscriptionStore.finalPrice) }} / {{ interval.short }}
            </span>
            <span class="page-sales-funnel__frequency">{{ interval.long }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="page-sales-funnel__body">
        <component :is="selectedForm"></component>
      </div>

      <div class="page-sales-funnel__footer">
        <div class="page-sales-funnel__footer-wrapper">
          <span class="page-sales-funnel__label">{{ $t('Do you need help?') }}</span>
          <span
            class="page-sales-funnel__contact"
            v-html="$t('Contact us at email', { email: $t('email-subscription') })"
          />
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.page-sales-funnel__header {
  margin-bottom: 0;
  padding: 16px 0;
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: var(--color-surface-primary) !important;
  border-bottom: 1px solid #ccc;
}

.page-sales-funnel__subscription-name {
  font-size: 22px;
  font-weight: 600;
  font-variation-settings: "wdth" 88;
  padding-bottom: 4px;
}
</style>
